import { AssetMetadata } from '@/asset/api/types';
import { MetadataFieldRenderer } from '@/asset/components/metadata/field-renderer';
import { AssetMetadataForm } from '@/asset/utilities/metadata';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { groupBy } from 'lodash-es';
import { useMemo } from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';

interface MetadataFormProps {
  /**
   * Array of metadata to render in the form
   */
  metadata: AssetMetadata[];
}

/**
 * Renders a form for editing asset metadata.
 *
 * IMPORTANT: This component must be wrapped in a FormProvider from react-hook-form.
 * The form must have a `metadata` field that is an array of AssetMetadata objects.
 */
export function MetadataForm(props: MetadataFormProps) {
  const { metadata } = props;
  const { control, setValue } = useFormContext<AssetMetadataForm>();
  const sections = useMemo(() => {
    const grouped = groupBy(metadata, ({ field }) => field.section);
    const result: AssetMetadata[][] = Array.from(Object.values(grouped));

    return result;
  }, [metadata]);

  const isEmpty = sections.every((section) => section.length === 0);

  if (isEmpty) {
    return (
      <Typography
        color="text.secondary"
        textAlign="center"
        gridColumn={{ xs: 'span 1', sm: 'span 2' }}
      >
        This asset has no metadata
      </Typography>
    );
  }

  return (
    <>
      {sections.map((section, index) => {
        const multilineFields = section.filter(
          ({ field }) => field.type === 'multiline_text'
        );

        const rowCount = section.length + multilineFields.length;

        return (
          <Box
            key={index}
            gap={2}
            display="grid"
            gridRow={`span ${rowCount}`}
            gridTemplateRows="subgrid"
          >
            {section.map((metadataItem) => {
              const fieldIndex = metadata.findIndex(
                ({ field }) => field.id === metadataItem.field.id
              );
              return (
                <Controller
                  key={metadataItem.field.key}
                  render={({ fieldState }) => {
                    return (
                      <MetadataFieldRenderer
                        key={metadataItem.field.key}
                        metadata={metadataItem}
                        fieldIndex={fieldIndex}
                        setValue={(val) => {
                          setValue(`metadata.${fieldIndex}.value`, val, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }}
                        error={fieldState.error}
                      />
                    );
                  }}
                  name={`metadata.${fieldIndex}`}
                  control={control}
                />
              );
            })}
          </Box>
        );
      })}
    </>
  );
}

/**
 * Renders a list-style form for editing asset metadata.
 *
 * IMPORTANT: This component must be wrapped in a FormProvider from react-hook-form.
 * The form must have a `metadata` field that is an array of AssetMetadata objects.
 */
export function MetadataListForm(props: MetadataFormProps) {
  const { metadata } = props;
  const { control, setValue } = useFormContext<FieldValues>();
  const sortedFields = useMemo(() => {
    // Group by section
    const grouped = groupBy(metadata, ({ field }) => field.section);

    // Create an array to hold our sorted results
    let result: AssetMetadata[] = [];

    // For each section group (sorted first by section number)
    Object.entries(grouped)
      // Section gets converted to a string because it's now an object key
      .sort(([sectionA], [sectionB]) => Number(sectionA) - Number(sectionB))
      .forEach(([_, sectionFields]) => {
        // Sort fields within this section by order
        const sortedSectionFields = [...sectionFields].sort(
          (a, b) => (a.field.order || 0) - (b.field.order || 0)
        );

        // Add these sorted fields to our result
        result = [...result, ...sortedSectionFields];
      });

    return result;
  }, [metadata]);
  if (metadata.length == 0) {
    return (
      <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
        <Typography
          color="text.secondary"
          textAlign="center"
          gridColumn={{ xs: 'span 1', sm: 'span 2' }}
        >
          This asset has no metadata
        </Typography>
      </Paper>
    );
  }

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
        <Grid container spacing={2}>
          {sortedFields.map((metadataItem) => {
            const fieldIndex = metadata.findIndex(
              ({ field }) => field.id === metadataItem.field.id
            );
            return (
              <Grid item xs={12} key={metadataItem.field.key}>
                <Controller
                  render={({ fieldState }) => {
                    return (
                      <MetadataFieldRenderer
                        key={metadataItem.field.key}
                        metadata={metadataItem}
                        fieldIndex={fieldIndex}
                        setValue={(val) => {
                          setValue(`metadata.${fieldIndex}.value`, val, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }}
                        error={fieldState.error}
                      />
                    );
                  }}
                  name={`metadata.${fieldIndex}`}
                  control={control}
                />
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Grid>
  );
}
